import React, { useState, useCallback } from "react";
import { Pressable, View, ActivityIndicator } from "react-native";
import { Text } from "@src/packages/ui/Text/Text";
import { DynamicModal } from "@src/packages/ui/DynamicModal/DynamicModal";
import {
  X,
  Calendar,
  PenLine,
  Trash2,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react-native";
import { useDeleteAppointment } from "@src/packages/care/useCalendarData";
import { CalendarItem } from "@src/packages/care/calendar.types";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";

export interface EditAppointmentData {
  appointmentId: string;
  title: string;
  description?: string | null;
  startDate: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  consultationType?: string[] | null;
  isOnline?: boolean | null;
  meetingLink?: string | null;
  meetingRoom?: string | null;
}

interface AppointmentActionsModalProps {
  open: boolean;
  onClose: () => void;
  appointment: CalendarItem | null;
  onEdit: (data: EditAppointmentData) => void;
  onDeleted: () => void;
}

type ModalView = "actions" | "confirm-delete" | "success";

export function AppointmentActionsModal({
  open,
  onClose,
  appointment,
  onEdit,
  onDeleted,
}: AppointmentActionsModalProps) {
  const [view, setView] = useState<ModalView>("actions");
  const [successMessage, setSuccessMessage] = useState("");
  const capturePress = usePressCapture("AppointmentActionsModal");

  const deleteMutation = useDeleteAppointment();
  const isLoading = deleteMutation.isPending;

  const handleEdit = useCallback(() => {
    if (!appointment?.meta?.appointmentId) return;

    const [datePart, timePart] = appointment.startAt.split("T");

    const editData: EditAppointmentData = {
      appointmentId: appointment.meta.appointmentId,
      title: appointment.title,
      description: appointment.meta?.notes ?? null,
      startDate: datePart ?? "",
      startTime: timePart?.slice(0, 5),
      consultationType: appointment.meta?.consultationType ?? null,
      isOnline: appointment.meta?.isOnline ?? null,
      meetingLink: appointment.meta?.meetingLink ?? null,
      meetingRoom: appointment.meta?.location ?? null,
    };

    onEdit(editData);
  }, [appointment, onEdit]);

  const handleConfirmDelete = useCallback(async () => {
    if (!appointment?.meta?.appointmentId) return;

    try {
      await deleteMutation.mutateAsync(appointment.meta.appointmentId);

      setSuccessMessage("Appointment deleted");
      setView("success");

      setTimeout(() => {
        setView("actions");
        setSuccessMessage("");
        onDeleted();
      }, 800);
    } catch (error) {
      console.error("[AppointmentActionsModal] Failed to delete:", error);
      setView("actions");
    }
  }, [appointment, deleteMutation, onDeleted]);

  const handleClose = useCallback(() => {
    setView("actions");
    setSuccessMessage("");
    onClose();
  }, [onClose]);

  if (!appointment) return null;

  return (
    <DynamicModal open={open} onClose={handleClose} size={420}>
      <View className="px-6 pb-8 pt-6">
        {/* Close button */}
        <View className="absolute right-3 top-3 z-10">
          <Pressable
            onPress={() => {
              capturePress("Close modal");
              handleClose();
            }}
            disabled={isLoading}
            className="rounded-full p-2"
            accessibilityRole="button"
            accessibilityLabel="Close modal"
          >
            <X size={20} color="#64748B" />
          </Pressable>
        </View>

        {/* Success view */}
        {view === "success" && (
          <View className="items-center py-8">
            <View
              className="mb-4 h-16 w-16 items-center justify-center rounded-full"
              style={{ backgroundColor: "rgba(74, 222, 128, 0.15)" }}
            >
              <CheckCircle2 size={32} color="#4ADE80" />
            </View>
            <Text
              variant="title"
              size="lg"
              className="text-lg font-semibold text-white"
            >
              {successMessage}
            </Text>
          </View>
        )}

        {/* Confirm delete view */}
        {view === "confirm-delete" && (
          <View>
            <View className="mb-6 items-center">
              <View
                className="mb-4 h-14 w-14 items-center justify-center rounded-2xl"
                style={{ backgroundColor: "rgba(239, 68, 68, 0.15)" }}
              >
                <AlertTriangle size={28} color="#F87171" />
              </View>
              <Text
                variant="title"
                size="lg"
                className="mb-2 text-center text-lg font-bold text-white"
              >
                Delete {appointment.title}?
              </Text>
              <Text
                variant="body"
                size="sm"
                className="text-center text-sm leading-5 text-slate-400"
              >
                This will permanently remove this appointment. This action
                cannot be undone.
              </Text>
            </View>

            <View className="gap-3">
              <Pressable
                onPress={() => {
                  capturePress(
                    "Yes, delete permanently",
                    "confirm_delete_appointment",
                  );
                  void handleConfirmDelete();
                }}
                disabled={isLoading}
                className={`w-full flex-row items-center justify-center gap-2 rounded-xl py-3.5 ${isLoading ? "opacity-70" : ""}`}
                style={{ backgroundColor: "#EF4444" }}
                accessibilityRole="button"
              >
                {isLoading ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Trash2 size={18} color="#ffffff" />
                )}
                <Text
                  variant="body"
                  size="sm"
                  className="font-semibold text-white"
                >
                  {isLoading ? "Deleting..." : "Yes, delete permanently"}
                </Text>
              </Pressable>

              <Pressable
                onPress={() => {
                  capturePress("Cancel", "cancel_delete_appointment");
                  setView("actions");
                }}
                disabled={isLoading}
                className="w-full flex-row items-center justify-center rounded-xl border border-white/10 bg-white/5 py-3.5"
                accessibilityRole="button"
              >
                <Text
                  variant="body"
                  size="sm"
                  className="font-semibold text-slate-300"
                >
                  Cancel
                </Text>
              </Pressable>
            </View>
          </View>
        )}

        {/* Actions view (default) */}
        {view === "actions" && (
          <View>
            {/* Header */}
            <View className="mb-6 items-center">
              <View
                className="mb-4 h-14 w-14 items-center justify-center rounded-2xl"
                style={{
                  backgroundColor: "rgba(96, 165, 250, 0.15)",
                  shadowColor: "#60A5FA",
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: 0.5,
                  shadowRadius: 20,
                }}
              >
                <Calendar size={28} color="#60A5FA" />
              </View>

              <Text
                variant="title"
                size="lg"
                className="mb-2 text-center text-xl font-bold text-white"
              >
                {appointment.title}
              </Text>

              {appointment.subtitle && (
                <Text
                  variant="body"
                  size="sm"
                  className="text-center text-sm text-slate-400"
                >
                  {appointment.subtitle}
                </Text>
              )}
            </View>

            {/* Separator */}
            <View className="mb-5 h-px bg-white/10" />

            {/* Action Buttons */}
            <View className="gap-3">
              {/* Edit */}
              <Pressable
                onPress={() => {
                  capturePress("Edit appointment");
                  handleEdit();
                }}
                className="w-full flex-row items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 py-3.5"
                accessibilityRole="button"
                accessibilityLabel="Edit appointment"
              >
                <PenLine size={18} color="#60A5FA" />
                <Text
                  variant="body"
                  size="sm"
                  className="font-semibold text-white"
                >
                  Edit appointment
                </Text>
              </Pressable>

              {/* Delete */}
              <Pressable
                onPress={() => {
                  capturePress("Delete appointment");
                  setView("confirm-delete");
                }}
                className="w-full flex-row items-center justify-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 py-3.5"
                accessibilityRole="button"
                accessibilityLabel="Delete appointment"
              >
                <Trash2 size={18} color="#F87171" />
                <Text
                  variant="body"
                  size="sm"
                  className="font-semibold"
                  style={{ color: "#F87171" }}
                >
                  Delete appointment
                </Text>
              </Pressable>
            </View>
          </View>
        )}
      </View>
    </DynamicModal>
  );
}
